// web/src/theme/applyTheme/_hostTheme.ts —— 内部：把本机主题下发一份「宿主令牌快照」给同源嵌入页
//
// 为什么需要它：知识问答的三个菜单与工作台「智能辅助」卡都是 WeKnora（另一套 TDesign）的 iframe。
// 那边看不到本系统的 CSS 变量，此前靠 `bcc-embed.css` 手抄一份蓝值——只有默认主题对得上，
// 用户换主题 / 改圆角 / 改线框档之后两边立刻两套颜色（详见方案 A）。
//
// 机制：applyTheme 已经把最终值写进 html/body（默认主题时来自 tokens.css，非默认时来自行内），
// 这里不再重算派生逻辑，直接 **读计算样式** 落成一份 JSON 快照。这样与真实渲染零漂移，
// 新增 token 时也只需扩 `HOST_THEME_VARS` 一处。
//
// 为什么走 localStorage 而不是 postMessage：bcc 里四个嵌入点只有「智能辅助」卡有桥，
// 三个知识菜单是裸 iframe；而 postMessage 只能在子页挂载后到达，首帧会闪一下旧主题。
// 同源存储 + storage 事件可以做到首帧正确、切主题实时跟随，且不必给协议加字段。
// 颜色不承载任何授权信息，落存储不增加暴露面。
//
// 写入方（本文件）在 bcc 页面里；读取方在 WeKnora 的 `utils/hostTheme.ts`，那里的映射表
// 认的就是 `--bcc-*` 这套变量名——两边契约以变量名为准，值怎么算由本系统负责。

/** 快照的存储键：与 WeKnora 侧 `utils/hostTheme.ts` 的 HOST_THEME_KEY 必须一致 */
export const HOST_THEME_KEY = 'bcc.host-theme';

/**
 * 快照版本号：WeKnora 只认自己认识的键、未知键忽略。
 * 下次若改了键名或语义（不是加键），把它 +1，让子页按版本分支而不是按存在性猜。
 */
export const HOST_THEME_VERSION = 1;

/**
 * 下发给嵌入页的变量白名单（都是 :root 上的 `--bcc-*`）。
 * 只放「跨系统需要对齐的观感」，不放布局骨架（侧栏宽、页头高之类子页用不到）。
 * 加一项前先问：WeKnora 侧有没有对应落点？没有就别加，白名单越长越难维护。
 */
export const HOST_THEME_VARS = [
  // 品牌与 AI
  '--bcc-primary',
  '--bcc-primary-deep',
  '--bcc-primary-bg',
  '--bcc-primary-rgb',
  '--bcc-ai',
  '--bcc-ai-deep',
  '--bcc-ai-bg',
  '--bcc-ai-rgb',
  '--bcc-highlight-rgb',
  // 底与文字
  '--bcc-bg',
  '--bcc-bg-card',
  '--bcc-text-1',
  '--bcc-text-2',
  '--bcc-text-3',
  // 描边（装饰档 + 可交互控件档）
  '--bcc-border',
  '--bcc-border-strong',
  '--bcc-border-control',
  '--bcc-border-control-hover',
  // 功能色（不随主题变，但两边必须同值：告警条、状态点、危险按钮）
  '--bcc-success',
  '--bcc-warning',
  '--bcc-danger',
  '--bcc-link-success',
  '--bcc-link-warning',
  '--bcc-link-danger',
  // 圆角与阴影
  '--bcc-radius-card',
  '--bcc-radius-btn',
  '--bcc-radius-input',
  '--bcc-radius-sm',
  '--bcc-radius-pill',
  '--bcc-shadow-card',
  '--bcc-shadow-float',
  '--bcc-shadow-hover',
  // 表格与渐变
  '--bcc-th-bg',
  '--bcc-th-bg-solid',
  '--bcc-row-hover-solid',
  '--bcc-table-cell-y',
  '--bcc-grad-primary',
  '--bcc-grad-ai',
  '--bcc-focus-ring',
  // 滚动条（嵌入页整块可滚，要和宿主同粗细同色）
  '--bcc-scroll-thumb',
  '--bcc-scroll-thumb-hover',
  '--bcc-scroll-radius',
  // 字号与间距（子页标题/内边距对齐用）
  '--bcc-font-base',
  '--bcc-font-md',
  '--bcc-font-lg',
  '--bcc-font-xl',
  '--bcc-space-1',
  '--bcc-space-2',
  '--bcc-space-3',
  '--bcc-space-4',
] as const;

/** 快照结构：与 WeKnora 侧解析的字段一一对应 */
export interface HostThemeSnapshot {
  /** 契约版本，见 HOST_THEME_VERSION */
  v: number;
  /** 主题键（bcc.theme），仅供排查用，子页不据此分支 */
  theme: string;
  /** 各偏好的档位键，同上，仅供排查 */
  prefs: { border: string; density: string; motion: string };
  /** 变量名 → 计算值（不含空值） */
  vars: Record<string, string>;
}

/**
 * 读当前计算样式并写快照。
 *
 * 必须在 `writeVars(...)` 之后调用：默认主题时行内没有值，要从 tokens.css 的 `:root` 取；
 * 非默认主题时行内值同样会体现在计算样式里——两种情况用同一条读取路径，不必分支。
 * 调用点只有 applyTheme 一处（所有偏好 setter 都汇聚到它）。
 */
export function emitHostTheme(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    // 从 body 读而不是 html：自定义属性会继承，html 上的值在 body 一样读得到；
    // 但「控件描边」这一组（--bcc-border-control / -hover）是写 body 的（Arco 的变量宿主是 body），
    // 只读 html 会拿到 tokens.css 的默认档，导致宿主切「标准/强调」时嵌入页的输入框描边不跟。
    const host = document.body ?? document.documentElement;
    const cs = getComputedStyle(host);
    const vars: Record<string, string> = {};
    for (const name of HOST_THEME_VARS) {
      const value = cs.getPropertyValue(name).trim();
      if (value) vars[name] = value;
    }
    // 一个都没读到说明样式表还没生效（首屏极早期）：不写空快照，免得子页把"无数据"当"无主题"
    if (Object.keys(vars).length === 0) return;

    const snapshot: HostThemeSnapshot = {
      v: HOST_THEME_VERSION,
      theme: document.body?.dataset?.bccTheme || '',
      prefs: {
        border: document.body?.dataset?.bccBorder || '',
        density: document.body?.dataset?.bccDensity || '',
        motion: document.body?.dataset?.bccMotion || '',
      },
      vars,
    };
    localStorage.setItem(HOST_THEME_KEY, JSON.stringify(snapshot));
  } catch {
    // 隐私模式 / 存储配额 / 无 localStorage：嵌入页退回它自己的兜底皮肤，不影响宿主渲染
  }
}
